const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/useDrawingUndoStack.ts';
import { useCallback, useRef, useState } from 'react';
import React from 'react';

interface UseDrawingUndoStackArgs {
    drawingAreaRefs: React.MutableRefObject<Map<number, React.RefObject<any>>>;
}

export function useDrawingUndoStack({ drawingAreaRefs }: UseDrawingUndoStackArgs) {
    // One entry per stroke, in draw order; value = page number
    const drawStackRef = useRef<number[]>([]);
    const pathCountsRef = useRef<Map<number, number>>(new Map());
    const [hasActiveDrawing, setHasActiveDrawing] = useState(false);

    const countPaths = useCallback((page: number) => {
        const svg = drawingAreaRefs.current.get(page)?.current?.getSvgXml() ?? '';
        return (svg.match(/<path/g) ?? []).length;
    }, []);

    /**
     * Reconciles the stack against the actual path count of a page.
     * Wire this to DrawingArea's onChange for every page — it handles
     * both added strokes and strokes removed by undo.
     */
    const handleDrawingChange = useCallback(
        (page: number) => {
            const count = countPaths(page);
            const prev = pathCountsRef.current.get(page) ?? 0;

            if (count > prev) {
                for (let i = prev; i < count; i++) drawStackRef.current.push(page);
            } else if (count < prev) {
                let toRemove = prev - count;
                for (let i = drawStackRef.current.length - 1; i >= 0 && toRemove > 0; i--) {
                    if (drawStackRef.current[i] === page) {
                        drawStackRef.current.splice(i, 1);
                        toRemove--;
                    }
                }
            }

            pathCountsRef.current.set(page, count);
            setHasActiveDrawing(drawStackRef.current.length > 0);
        },
        [countPaths]
    );

    /** Undoes the most recent stroke, regardless of which page it's on. */
    const undo = useCallback(() => {
        const stack = drawStackRef.current;
        const page = stack[stack.length - 1]; // peek — handleDrawingChange reconciles after DrawingArea.undo()
        if (page === undefined) return;

        drawingAreaRefs.current.get(page)?.current?.undo();
    }, []);

    /** Call after committing drawings to the PDF or when canvases are recreated. */
    const reset = useCallback(() => {
        drawStackRef.current = [];
        pathCountsRef.current.clear();
        setHasActiveDrawing(false);
    }, []);

    return { handleDrawingChange, hasActiveDrawing, reset, undo };
}
